import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import { format } from "date-fns";
import { NumericFormat } from "react-number-format";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import "./FormInvoiceRegister.css";
import "react-datepicker/dist/react-datepicker.css";
import axiosInstance from "../../../Config/axiosInstance";

//Înregistrează localizarea română pentru DatePicker.
registerLocale("ro", ro);

const animatedComponents = makeAnimated();

//Stiluri pentru meniurile react-select afișate în portal,astfel încât dropdown-urile să apară peste celelalte elemente din pagină.
const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
};

//Formatează o dată în format YYYY-MM-DD într-un format vizibil pentru utilizator: DD.MM.YYYY.
const formatDateLabel = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

//Formatează o valoare numerică pentru afișare cu separator de mii și două zecimale.
const formatAmountDisplay = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0,00";

  return new Intl.NumberFormat("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const AddInvoice = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [addendums, setAddendums] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [contractOpportunities, setContractOpportunities] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedAddendum, setSelectedAddendum] = useState(null);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedExchangeRate, setSelectedExchangeRate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedOpps, setSelectedOpps] = useState([]);
  const [selectedContractPaperDate, setSelectedContractPaperDate] = useState(null);
  const [selectedInvoiceDate, setSelectedInvoiceDate] = useState(null);
  const [selectedInvoiceDueDate, setSelectedInvoiceDueDate] = useState(null);
  const [selectedReceiptDueDate, setSelectedReceiptDueDate] = useState(null);
  const [errorNotification, setErrorNotification] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isOppLoading, setIsOppLoading] = useState(false);
  const [isAddendumLoading, setIsAddendumLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  //Controlează ce secțiuni sunt expandate sau restrânse în formular.
  const [expandedSections, setExpandedSections] = useState({
    invoice: true,
    amounts: true,
    additional: true,
  });

  // Starea principală a formularului.
  const [formData, setFormData] = useState({
    invoice_number: "",
    contract: "",
    addendum: "",
    client: "",
    project_code: "",
    project_responsible: "",
    contract_paper_date: "",
    invoice_date: "",
    invoice_due_date: "",
    currency: "",
    exchange_rate: "",
    receipt_due_date: "",
    status: "",
    description: "",
  });

  //Liniile facturii asociate oportunităților selectate.
  const [lines, setLines] = useState([]);

  // Opțiunile fixe pentru statusul facturii.
  const statusOptions = [
    { value: "Compliant invoice", label: "Compliant invoice" },
    { value: "Invoice issued", label: "Invoice issued" },
  ];

  //Deschide sau închide o secțiune a formularului.
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  //Afișează un mesaj de eroare temporar în partea de sus a formularului.
  const showError = useCallback((message, timeout = 5000) => {
    setErrorNotification(message);
    setTimeout(() => setErrorNotification(""), timeout);
  }, []);

  //Grupează cursurile valutare după monedă și le sortează descrescător după dată, astfel încât cel mai recent curs să fie primul.
  const exchangeOptionsByCurrencyId = useMemo(() => {
    const m = new Map();

    for (const r of exchangeRates) {
      const curId = Number(r.currency);
      if (!m.has(curId)) m.set(curId, []);

      m.get(curId).push({
        value: r.id,
        label: String(r.value ?? ""),
        dateLabel: r.date || "",
        rateValue: Number(r.value),
        rateDate: r.date,
      });
    }

    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      m.set(k, arr);
    }

    return m;
  }, [exchangeRates]);

  //Calculează totalul general al facturii cu TVA pentru toate liniile selectate.
  const totalInvoiceDisplay = useMemo(() => {
    if (!lines.length) return "0,00";

    const total = lines.reduce((acc, line) => {
      const val = Number(line.sum_with_vat);
      return acc + (Number.isFinite(val) ? val : 0);
    }, 0);

    return `${formatAmountDisplay(total)} ${selectedCurrency?.label || ""}`.trim();
  }, [lines, selectedCurrency]);

  //Verifică dacă moneda selectată este aceeași cu moneda de sistem.
  const isSystemCurrency = useMemo(() => {
    if (!formData.currency || !systemCurrencyId) return false;
    return Number(formData.currency) === Number(systemCurrencyId);
  }, [formData.currency, systemCurrencyId]);

  //Returnează lista de cursuri valutare disponibile pentru moneda selectată.
  const exchangeOptions = useMemo(() => {
    if (!formData.currency) return [];
    return exchangeOptionsByCurrencyId.get(Number(formData.currency)) || [];
  }, [formData.currency, exchangeOptionsByCurrencyId]);

  /*
    Recalculează valorile unei linii:
    - sumă cu TVA
    - sumă în moneda de sistem fără TVA
    - sumă în moneda de sistem cu TVA
   */
  const recalculateLine = useCallback(
    (line) => {
      const sumWithoutVat = Number(line.sum_without_vat);
      const vat = Number(line.vat);

      const validSum = Number.isFinite(sumWithoutVat) ? sumWithoutVat : 0;
      const validVat = Number.isFinite(vat) ? vat : 0;

      const totalWithVat = validSum * (1 + validVat / 100);

      let systemWithoutVat = "";
      let systemWithVat = "";

      if (formData.currency && systemCurrencyId) {
        if (Number(formData.currency) === Number(systemCurrencyId)) {
          systemWithoutVat = validSum.toFixed(2);
          systemWithVat = totalWithVat.toFixed(2);
        } else {
          const rateValue = Number(selectedExchangeRate?.rateValue);
          if (Number.isFinite(rateValue) && rateValue > 0) {
            systemWithoutVat = (validSum * rateValue).toFixed(2);
            systemWithVat = (totalWithVat * rateValue).toFixed(2);
          }
        }
      }

      return {
        ...line,
        sum_with_vat: totalWithVat.toFixed(2),
        sum_system_without_vat: systemWithoutVat,
        sum_with_vat_system: systemWithVat,
      };
    },
    [formData.currency, selectedExchangeRate, systemCurrencyId]
  );

  //Recalculează toate liniile ori de câte ori se schimbă moneda, cursul valutar sau logica de calcul.
  useEffect(() => {
    setLines((prev) => prev.map((line) => recalculateLine(line)));
  }, [recalculateLine]);

  /*
    La încărcarea componentei:
    - aduce contractele
    - angajații
    - monedele
    - cursurile valutare
    - moneda de sistem
   */
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);

      try {
        const [
          contractsRes,
          employeesRes,
          currenciesRes,
          exchangeRes,
          systemRes,
        ] = await Promise.all([
          axiosInstance.get("/client-contracts/"),
          axiosInstance.get("/employees/"),
          axiosInstance.get("/currencies/"),
          axiosInstance.get("/exchange-rate/"),
          axiosInstance.get("/system-currency/"),
        ]);

        setContracts(
          (contractsRes.data || []).map((contract) => ({
            value: contract.id,
            label: contract.nr || `Contract ${contract.id}`,
            client_label: contract.client_name || contract.client_label || "",
            project_code_label:
              contract.project_code_label ||
              contract.project_code?.code ||
              contract.project_code_code ||
              "",
            responsible_person: contract.responsible_person,
            responsible_person_label:
              contract.responsible_person_label ||
              contract.responsible_person_name ||
              "",
            sign_date: contract.sign_date || contract.sign_date_label || "",
            raw: contract,
          }))
        );

        setEmployees(
          (employeesRes.data || []).map((emp) => ({
            value: emp.id,
            label:
              `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
              `Employee ${emp.id}`,
          }))
        );

        setCurrencies(
          (currenciesRes.data || []).map((curr) => ({
            value: curr.id,
            label: curr.abbreviation || curr.name,
            abbreviation: curr.abbreviation,
            name: curr.name,
          }))
        );

        setExchangeRates(
          (exchangeRes.data || []).sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          )
        );

        if (Array.isArray(systemRes.data) && systemRes.data.length > 0) {
          const currencyId = systemRes.data[0]?.currency ?? null;
          setSystemCurrencyId(currencyId || null);

          const allCurrencies = (currenciesRes.data || []).map((curr) => ({
            value: curr.id,
            abbreviation: curr.abbreviation,
            name: curr.name,
          }));

          const sys = allCurrencies.find(
            (c) => Number(c.value) === Number(currencyId)
          );
          setSystemCurrencyLabel(sys?.abbreviation || sys?.name || "");
        }
      } catch (error) {
        console.error("Initial load error:", error);
        showError("Could not load invoice form data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [showError]);

  useEffect(() => {
    if (!formData.contract) {
      setAddendums([]);
      setSelectedAddendum(null);

      setFormData((prev) => ({
        ...prev,
        addendum: "",
      }));

      return;
    }

    const fetchAddendums = async () => {
      setIsAddendumLoading(true);

      try {
        const response = await axiosInstance.get(
          `/client-addendums/?contract=${formData.contract}`
        );

        const formattedAddendums = (response.data || []).map((addendum) => ({
          value: addendum.id,
          label: addendum.nr || `Addendum ${addendum.id}`,

          client: addendum.client,
          client_label:
            addendum.client_name ||
            addendum.client_label ||
            "",

          responsible_person: addendum.responsible_person,
          responsible_person_label:
            addendum.responsible_person_label ||
            addendum.responsible_person_name ||
            "",

          sign_date:
            addendum.sign_date ||
            addendum.sign_date_label ||
            "",

          project_code_id: addendum.project_code_id,
          project_code_label:
            addendum.project_code_label ||
            "",

          lines: addendum.lines || [],
          raw: addendum,
        }));

        setAddendums(formattedAddendums);
      } catch (error) {
        console.error("Addendums fetch error:", error);

        setAddendums([]);
        setSelectedAddendum(null);

        setFormData((prev) => ({
          ...prev,
          addendum: "",
        }));
      } finally {
        setIsAddendumLoading(false);
      }
    };

    fetchAddendums();
  }, [formData.contract]);

  /* Când se selectează un contract:
    - aduce oportunitățile asociate acelui contract
    - actualizează automat project_code pe baza oportunităților găsite
    - resetează liniile dacă nu există contract selectat
   */
  useEffect(() => {
    if (!formData.contract) {
      setContractOpportunities([]);
      setSelectedOpps([]);
      setLines([]);

      setFormData((prev) => ({
        ...prev,
        project_code: "",
      }));

      return;
    }

    // Când este ales addendumul, oportunitățile sunt deja
    // preluate din liniile addendumului.
    if (selectedAddendum) {
      return;
    }

    const fetchContractOpportunities = async () => {
      setIsOppLoading(true);

      try {
        const response = await axiosInstance.get(
          `/opportunities/?contract=${formData.contract}`
        );

        const formatted = (response.data || []).map((opp) => ({
          value: opp.id,

          label:
            `${opp.code_opportunity ?? ""} - ${opp.title_opportunity ?? ""
              }`.trim() ||
            opp.project_name ||
            `Opportunity ${opp.id}`,

          project_code: opp.project_code,

          project_code_label:
            opp.project_code_label ||
            opp.project_code?.code ||
            "",

          subproject_code:
            opp.subproject_code,

          subproject_code_label:
            opp.subproject_code_label ||
            opp.subproject_code?.code ||
            "",

          client: opp.client,
        }));

        setContractOpportunities(formatted);

        const uniqueCodes = [
          ...new Set(
            formatted
              .map((opp) => opp.project_code_label)
              .filter(Boolean)
          ),
        ];

        setFormData((prev) => ({
          ...prev,
          project_code: uniqueCodes.join(", "),
        }));
      } catch (error) {
        console.error(
          "Opportunities fetch error:",
          error
        );

        setContractOpportunities([]);

        setFormData((prev) => ({
          ...prev,
          project_code:
            selectedContract?.project_code_label || "",
        }));
      } finally {
        setIsOppLoading(false);
      }
    };

    fetchContractOpportunities();
  }, [
    formData.contract,
    selectedContract,
    selectedAddendum,
  ]);

  /*Gestionează schimbarea contractului:
    - actualizează clientul și project code-ul
    - resetează oportunitățile și liniile anterioare
   */
  const handleContractChange = (selectedOption) => {
    setSelectedContract(selectedOption);

    setSelectedAddendum(null);
    setAddendums([]);

    setSelectedOpps([]);
    setLines([]);
    setContractOpportunities([]);

    const responsibleId =
      selectedOption?.responsible_person || "";

    const responsibleOption = responsibleId
      ? {
        value: responsibleId,
        label:
          selectedOption?.responsible_person_label ||
          employees.find(
            (employee) =>
              Number(employee.value) === Number(responsibleId)
          )?.label ||
          `Employee ${responsibleId}`,
      }
      : null;

    const contractSignDate =
      selectedOption?.sign_date || "";

    const contractSignDateObj = contractSignDate
      ? new Date(`${contractSignDate}T00:00:00`)
      : null;

    setSelectedResponsible(responsibleOption);
    setSelectedContractPaperDate(contractSignDateObj);

    setFormData((prev) => ({
      ...prev,
      contract: selectedOption
        ? selectedOption.value
        : "",

      addendum: "",

      client: selectedOption
        ? selectedOption.client_label
        : "",

      project_code: selectedOption
        ? selectedOption.project_code_label || ""
        : "",

      project_responsible: responsibleOption
        ? responsibleOption.value
        : "",

      contract_paper_date: contractSignDate,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      contract: "",
      client: "",
      project_code: "",
      project_responsible: "",
      contract_paper_date: "",
      invoice_lines: "",
    }));
  };

  const handleAddendumChange = (selectedOption) => {
    setSelectedAddendum(selectedOption);

    setSelectedOpps([]);
    setLines([]);

    // Dacă addendumul este șters, revenim la datele contractului.
    if (!selectedOption) {
      const responsibleId =
        selectedContract?.responsible_person || "";

      const responsibleOption = responsibleId
        ? {
          value: responsibleId,
          label:
            selectedContract?.responsible_person_label ||
            employees.find(
              (employee) =>
                Number(employee.value) === Number(responsibleId)
            )?.label ||
            `Employee ${responsibleId}`,
        }
        : null;

      const contractSignDate =
        selectedContract?.sign_date || "";

      const contractSignDateObj = contractSignDate
        ? new Date(`${contractSignDate}T00:00:00`)
        : null;

      setSelectedResponsible(responsibleOption);
      setSelectedContractPaperDate(contractSignDateObj);

      setFormData((prev) => ({
        ...prev,
        addendum: "",

        client:
          selectedContract?.client_label || "",

        project_code:
          selectedContract?.project_code_label || "",

        project_responsible:
          responsibleOption?.value || "",

        contract_paper_date:
          contractSignDate,
      }));

      return;
    }

    const responsibleId =
      selectedOption.responsible_person || "";

    const responsibleOption = responsibleId
      ? {
        value: responsibleId,
        label:
          selectedOption.responsible_person_label ||
          employees.find(
            (employee) =>
              Number(employee.value) === Number(responsibleId)
          )?.label ||
          `Employee ${responsibleId}`,
      }
      : null;

    const addendumSignDate =
      selectedOption.sign_date || "";

    const addendumSignDateObj = addendumSignDate
      ? new Date(`${addendumSignDate}T00:00:00`)
      : null;

    const addendumLines =
      selectedOption.lines || [];

    const opportunityOptions = addendumLines.map((line) => {
      const opportunity =
        line.opportunity_details ||
        line.opportunity_data ||
        line;

      return {
        value:
          line.opportunity ||
          opportunity.id,

        label:
          line.opportunity_label ||
          `${opportunity.code_opportunity || ""} - ${opportunity.title_opportunity || ""
            }`.replace(/^ - | - $/g, "") ||
          opportunity.project_name ||
          `Opportunity ${line.opportunity || opportunity.id
          }`,

        project_code:
          line.project_code ||
          opportunity.project_code,

        project_code_label:
          line.project_code_label ||
          opportunity.project_code_label ||
          opportunity.project_code?.code ||
          "",

        subproject_code:
          line.subproject_code ||
          opportunity.subproject_code,

        subproject_code_label:
          line.subproject_code_label ||
          opportunity.subproject_code_label ||
          opportunity.subproject_code?.code ||
          "",
      };
    });

    const uniqueProjectCodes = [
      ...new Set(
        opportunityOptions
          .map((opportunity) =>
            opportunity.project_code_label
          )
          .filter(Boolean)
      ),
    ];

    setContractOpportunities(opportunityOptions);
    setSelectedResponsible(responsibleOption);
    setSelectedContractPaperDate(addendumSignDateObj);

    setFormData((prev) => ({
      ...prev,

      addendum: selectedOption.value,

      client:
        selectedOption.client_label || "",

      project_code:
        uniqueProjectCodes.length
          ? uniqueProjectCodes.join(", ")
          : selectedOption.project_code_label || "",

      project_responsible:
        responsibleOption?.value || "",

      contract_paper_date:
        addendumSignDate,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      client: "",
      project_code: "",
      project_responsible: "",
      contract_paper_date: "",
      invoice_lines: "",
    }));
  };

  // Gestionează schimbarea responsabilului de proiect.
  const handleResponsibleChange = (selectedOption) => {
    setSelectedResponsible(selectedOption);

    setFormData((prev) => ({
      ...prev,
      project_responsible: selectedOption ? selectedOption.value : "",
    }));

    setFieldErrors((prev) => ({
      ...prev,
      project_responsible: "",
    }));
  };

  /* Gestionează schimbarea monedei:
    - dacă este moneda de sistem, cursul nu este necesar
    - dacă este altă monedă, selectează automat ultimul curs disponibil
   */
  const handleCurrencyChange = (selectedOption) => {
    setSelectedCurrency(selectedOption);

    if (!selectedOption) {
      setSelectedExchangeRate(null);
      setFormData((prev) => ({
        ...prev,
        currency: "",
        exchange_rate: "",
      }));
    } else if (
      systemCurrencyId &&
      Number(selectedOption.value) === Number(systemCurrencyId)
    ) {
      setSelectedExchangeRate(null);
      setFormData((prev) => ({
        ...prev,
        currency: selectedOption.value,
        exchange_rate: "",
      }));
    } else {
      const optionsForCurrency =
        exchangeOptionsByCurrencyId.get(Number(selectedOption.value)) || [];
      const latestRate = optionsForCurrency.length ? optionsForCurrency[0] : null;

      setSelectedExchangeRate(latestRate);

      setFormData((prev) => ({
        ...prev,
        currency: selectedOption.value,
        exchange_rate: latestRate ? latestRate.value : "",
      }));
    }

    setFieldErrors((prev) => ({
      ...prev,
      currency: "",
      exchange_rate: "",
    }));
  };

  // Gestionează schimbarea cursului valutar.
  const handleExchangeRateChange = (selectedOption) => {
    setSelectedExchangeRate(selectedOption);

    setFormData((prev) => ({
      ...prev,
      exchange_rate: selectedOption ? selectedOption.value : "",
    }));

    setFieldErrors((prev) => ({
      ...prev,
      exchange_rate: "",
    }));
  };

  //Gestionează schimbarea statusului facturii.
  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);

    setFormData((prev) => ({
      ...prev,
      status: selectedOption ? selectedOption.value : "",
    }));

    setFieldErrors((prev) => ({
      ...prev,
      status: "",
    }));
  };

  // Gestionează actualizarea câmpurilor de tip dată, transformând valoarea în format YYYY-MM-DD pentru backend.
  const handleDateChange = (date, fieldName) => {
    const isoDate = date ? format(date, "yyyy-MM-dd") : "";

    setFormData((prev) => ({
      ...prev,
      [fieldName]: isoDate,
    }));

    if (fieldName === "contract_paper_date") setSelectedContractPaperDate(date);
    if (fieldName === "invoice_date") setSelectedInvoiceDate(date);
    if (fieldName === "invoice_due_date") setSelectedInvoiceDueDate(date);
    if (fieldName === "receipt_due_date") setSelectedReceiptDueDate(date);

    setFieldErrors((prev) => ({
      ...prev,
      [fieldName]: "",
    }));
  };

  //Handler generic pentru input-uri simple și textarea.
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  /*
    Gestionează selecția multiplă a oportunităților:
    - păstrează liniile deja existente
    - creează linii noi pentru oportunitățile nou selectate
    - aplică recalcularea automată a sumelor
   */
  const handleOppsMultiChange = (opts) => {
    const selected = opts || [];
    setSelectedOpps(selected);

    setLines((prevLines) => {
      const prevById = new Map(prevLines.map((l) => [l.opportunity, l]));
      const next = [];

      for (const o of selected) {
        const existing = prevById.get(o.value);

        if (existing) {
          next.push(recalculateLine(existing));
        } else {
          next.push(
            recalculateLine({
              opportunity: o.value,
              opportunity_label: o.label,
              project_code_label: o.project_code_label || "",
              subproject_code_label: o.subproject_code_label || "",
              sum_without_vat: "",
              vat: 21,
              sum_with_vat: "",
              sum_system_without_vat: "",
              sum_with_vat_system: "",
            })
          );
        }
      }

      return next;
    });

    setFieldErrors((prev) => ({
      ...prev,
      invoice_lines: "",
      project_code: "",
    }));
  };

  //Actualizează o linie de oportunitate și recalculează automat valorile dependente.
  const updateLine = (opportunityId, patch) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.opportunity !== opportunityId) return line;
        return recalculateLine({ ...line, ...patch });
      })
    );
  };

  //Elimină o oportunitate din selecție și șterge linia aferentă.
  const removeOpportunityLine = (opportunityId) => {
    setSelectedOpps((prev) => prev.filter((o) => o.value !== opportunityId));
    setLines((prev) => prev.filter((l) => l.opportunity !== opportunityId));
  };

  /* Validează formularul:
    - verifică toate câmpurile obligatorii
    - verifică existența cursului valutar dacă moneda nu este cea de sistem
    - verifică liniile de oportunități și valorile introduse
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.invoice_number?.trim()) newErrors.invoice_number = "Required";
    if (!formData.contract) newErrors.contract = "Required";
    if (!formData.client?.trim()) newErrors.client = "Required";
    if (!formData.project_code?.trim()) newErrors.project_code = "Required";
    if (!formData.project_responsible) newErrors.project_responsible = "Required";
    if (!formData.contract_paper_date) newErrors.contract_paper_date = "Required";
    if (!formData.invoice_date) newErrors.invoice_date = "Required";
    if (!formData.invoice_due_date) newErrors.invoice_due_date = "Required";
    if (!formData.currency) newErrors.currency = "Required";

    if (!isSystemCurrency && !formData.exchange_rate) {
      newErrors.exchange_rate = "Exchange rate required";
    }

    if (!formData.status) newErrors.status = "Required";
    if (!lines.length) newErrors.invoice_lines = "Select at least one opportunity";

    lines.forEach((line, idx) => {
      if (
        line.sum_without_vat === null ||
        line.sum_without_vat === undefined ||
        line.sum_without_vat === ""
      ) {
        newErrors[`line_${idx}_sum_without_vat`] = "Required";
      }

      if (line.vat === null || line.vat === undefined || line.vat === "") {
        newErrors[`line_${idx}_vat`] = "Required";
      }
    });

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* Trimite factura către backend:
    - validează datele
    - construiește payload-ul final
    - trimite factura și liniile aferente
    - revine la pagina anterioară după succes
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        invoice_number: formData.invoice_number,
        contract: Number(formData.contract),
        addendum: formData.addendum
          ? Number(formData.addendum)
          : null,
        project_responsible: Number(formData.project_responsible),
        contract_paper_date: formData.contract_paper_date,
        invoice_date: formData.invoice_date,
        invoice_due_date: formData.invoice_due_date,
        receipt_due_date: formData.receipt_due_date || null,
        currency: Number(formData.currency),
        exchange_rate: isSystemCurrency ? null : Number(formData.exchange_rate),
        status: formData.status,
        description: formData.description || "",
        invoice_lines: lines.map((line) => ({
          opportunity: Number(line.opportunity),
          sum_without_vat:
            line.sum_without_vat === "" || line.sum_without_vat === null
              ? null
              : Number(line.sum_without_vat),
          vat:
            line.vat === "" || line.vat === null
              ? null
              : Number(line.vat),
        })),
      };

      await axiosInstance.post("/invoices-register/", payload);
      navigate(-1);
    } catch (error) {
      console.error("Save error:", error.response?.data || error.message);

      if (error.response?.data) {
        const err = error.response.data;
        if (typeof err === "object") {
          const firstMessage =
            Object.values(err).flat().join(" | ") || JSON.stringify(err);
          showError(firstMessage);
        } else {
          showError("An error occurred while saving.");
        }
      } else {
        showError("An error occurred while saving.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Componentă reutilizabilă pentru afișarea erorilor sub câmpuri.
  const ErrorMessage = ({ error }) =>
    error ? (
      <div className="FormInvoiceRegister-error">
        <span className="error-icon">⚠️</span>
        <span>{error}</span>
      </div>
    ) : null;

  //Componentă reutilizabilă pentru header-ul secțiunilor.Permite expandare și restrângere.
  const SectionHeader = ({ title, section }) => (
    <div
      className="FormInvoiceRegister-section-header"
      onClick={() => toggleSection(section)}
    >
      <h2>{title}</h2>
      <button type="button" className="toggle-btn">
        {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </button>
    </div>
  );

  // Afișează loader cât timp se încarcă datele inițiale.
  if (isLoading) {
    return (
      <div className="FormInvoiceRegister">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading invoice form...</p>
        </div>
      </div>
    );
  }

  /*Randarea principală a formularului:
    - detalii factură
    - sume și oportunități
    - detalii suplimentare
    - acțiuni de salvare / anulare
   */
  return (
    <div className="FormInvoiceRegister">
      {errorNotification && (
        <div className="FormInvoiceRegister-top-error">{errorNotification}</div>
      )}

      <div className="FormInvoiceRegister-header">
        <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
        <h1 className="header-title">ADD INVOICE</h1>
        <div className="invoice-number-pill">
          Number: {formData.invoice_number || "-"}
        </div>
      </div>

      <hr className="title-separator" />

      <form onSubmit={handleSubmit} className="FormInvoiceRegister-form">
        <div className="FormInvoiceRegister-section">
          <SectionHeader title="INVOICE DETAILS" section="invoice" />

          {expandedSections.invoice && (
            <div className="section-content">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Invoice Series Number <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleChange}
                    className="FormInvoiceRegister-input"
                    autoComplete="off"
                  />
                  <ErrorMessage error={fieldErrors.invoice_number} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Contract Number <span className="required">*</span>
                  </label>
                  <Select
                    options={contracts}
                    classNamePrefix="FormInvoiceRegister-select"
                    value={selectedContract}
                    onChange={handleContractChange}
                    placeholder="Select contract"
                    components={animatedComponents}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.contract} />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Addendum
                  </label>

                  <Select
                    options={addendums}
                    classNamePrefix="FormInvoiceRegister-select"
                    value={selectedAddendum}
                    onChange={handleAddendumChange}
                    placeholder={
                      !formData.contract
                        ? "Select contract first"
                        : isAddendumLoading
                          ? "Loading addendums..."
                          : addendums.length
                            ? "Select addendum"
                            : "No addendums for this contract"
                    }
                    components={animatedComponents}
                    isClearable
                    isSearchable
                    isDisabled={
                      !formData.contract ||
                      isAddendumLoading ||
                      !addendums.length
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Client <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="client"
                    value={formData.client}
                    readOnly
                    className="readonly-field"
                  />
                  <ErrorMessage error={fieldErrors.client} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Project Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="project_code"
                    value={formData.project_code}
                    readOnly
                    className="readonly-field"
                  />
                  <ErrorMessage error={fieldErrors.project_code} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Project Responsible <span className="required">*</span>
                  </label>
                  <Select
                    options={employees}
                    classNamePrefix="FormInvoiceRegister-select"
                    value={selectedResponsible}
                    onChange={handleResponsibleChange}
                    placeholder=""
                    components={{
                      ...animatedComponents,
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                    }}
                    isClearable={false}
                    isSearchable={false}
                    isDisabled={true}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.project_responsible} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Contract / Addendum Date <span className="required">*</span>
                  </label>
                  <div className="date-input-wrapper">
                    <DatePicker
                      selected={selectedContractPaperDate}
                      onChange={(date) => handleDateChange(date, "contract_paper_date")}
                      dateFormat="dd.MM.yyyy"
                      locale="ro"
                      placeholderText=""
                      className="FormInvoiceRegister-date-input readonly-field"
                      disabled
                      popperContainer={({ children }) => (
                        <div style={{ position: "fixed", zIndex: 9999 }}>{children}</div>
                      )}
                    />
                  </div>
                  <ErrorMessage error={fieldErrors.contract_paper_date} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Invoice Date <span className="required">*</span>
                  </label>
                  <div className="date-input-wrapper">
                    <DatePicker
                      selected={selectedInvoiceDate}
                      onChange={(date) => handleDateChange(date, "invoice_date")}
                      dateFormat="dd.MM.yyyy"
                      locale="ro"
                      placeholderText="Select date"
                      className="FormInvoiceRegister-date-input"
                      popperContainer={({ children }) => (
                        <div style={{ position: "fixed", zIndex: 9999 }}>{children}</div>
                      )}
                    />
                    <span className="calendar-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                  </div>
                  <ErrorMessage error={fieldErrors.invoice_date} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Invoice Due Date <span className="required">*</span>
                  </label>
                  <div className="date-input-wrapper">
                    <DatePicker
                      selected={selectedInvoiceDueDate}
                      onChange={(date) => handleDateChange(date, "invoice_due_date")}
                      dateFormat="dd.MM.yyyy"
                      locale="ro"
                      placeholderText="Select date"
                      className="FormInvoiceRegister-date-input"
                      popperContainer={({ children }) => (
                        <div style={{ position: "fixed", zIndex: 9999 }}>{children}</div>
                      )}
                    />
                    <span className="calendar-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                  </div>
                  <ErrorMessage error={fieldErrors.invoice_due_date} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Currency <span className="required">*</span>
                  </label>
                  <Select
                    options={currencies}
                    classNamePrefix="FormInvoiceRegister-select"
                    value={selectedCurrency}
                    onChange={handleCurrencyChange}
                    placeholder="Select currency"
                    components={animatedComponents}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.currency} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Exchange Rate {!isSystemCurrency && <span className="required">*</span>}
                  </label>
                  <Select
                    options={exchangeOptions}
                    classNamePrefix="FormInvoiceRegister-select"
                    value={selectedExchangeRate}
                    onChange={handleExchangeRateChange}
                    placeholder={
                      !formData.currency
                        ? "Select currency first"
                        : isSystemCurrency
                          ? "Not needed"
                          : exchangeOptions.length
                            ? "Latest rate selected automatically"
                            : "No rates"
                    }
                    components={animatedComponents}
                    isClearable
                    isDisabled={!formData.currency || isSystemCurrency}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    formatOptionLabel={(option, { context }) =>
                      context === "value"
                        ? option.label
                        : `${option.label}${option.dateLabel ? ` (${formatDateLabel(option.dateLabel)})` : ""}`
                    }
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.exchange_rate} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="FormInvoiceRegister-section">
          <SectionHeader title="AMOUNTS" section="amounts" />

          {expandedSections.amounts && (
            <div className="section-content">
              <div className="form-group full-width">
                <label className="form-label">
                  Opportunities <span className="required">*</span>
                </label>

                <Select
                  isMulti
                  options={contractOpportunities}
                  classNamePrefix="FormInvoiceRegister-select"
                  value={selectedOpps}
                  onChange={handleOppsMultiChange}
                  placeholder={
                    !formData.contract
                      ? "Select contract first"
                      : isOppLoading
                        ? "Loading opportunities..."
                        : "Search and select opportunities..."
                  }
                  components={animatedComponents}
                  isDisabled={!formData.contract || isOppLoading}
                  noOptionsMessage={() => "No opportunities found"}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                />

                <ErrorMessage error={fieldErrors.invoice_lines} />
              </div>

              {lines.length === 0 ? (
                <div className="no-opportunities-message">
                  <AddIcon className="hint-icon" />
                  <span>No opportunities selected yet</span>
                </div>
              ) : (
                <div className="opportunities-grid">
                  {lines.map((line, idx) => (
                    <div key={line.opportunity} className="opportunity-item">
                      <div className="opportunity-item-header">
                        <div className="item-title">
                          <div className="item-title-text">
                            <h3>{line.opportunity_label}</h3>
                            <div className="item-subtitle">
                              {line.project_code_label && (
                                <span>Project Code: {line.project_code_label}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => removeOpportunityLine(line.opportunity)}
                          title="Remove opportunity"
                        >
                          <DeleteIcon />
                        </button>
                      </div>

                      <div className="opportunity-details">
                        {line.subproject_code_label ? (
                          <div className="detail-row single-detail-row">
                            <div className="detail-field">
                              <label className="form-label">Subproject Code</label>
                              <input
                                type="text"
                                value={line.subproject_code_label}
                                readOnly
                                className="readonly-field"
                              />
                            </div>
                          </div>
                        ) : null}

                        <div className="pricing-row">
                          <div className="pricing-field">
                            <label className="form-label">
                              Sum without VAT <span className="required">*</span>
                            </label>
                            <NumericFormat
                              value={line.sum_without_vat}
                              onValueChange={({ floatValue }) =>
                                updateLine(line.opportunity, {
                                  sum_without_vat: floatValue ?? "",
                                })
                              }
                              thousandSeparator="."
                              decimalSeparator=","
                              decimalScale={2}
                              allowNegative={false}
                              placeholder="0,00"
                              className="amount-input"
                            />
                            <ErrorMessage error={fieldErrors[`line_${idx}_sum_without_vat`]} />
                          </div>

                          <div className="pricing-field">
                            <label className="form-label">
                              VAT <span className="required">*</span>
                            </label>
                            <NumericFormat
                              value={line.vat}
                              onValueChange={({ floatValue }) =>
                                updateLine(line.opportunity, {
                                  vat: floatValue ?? "",
                                })
                              }
                              thousandSeparator="."
                              decimalSeparator=","
                              decimalScale={2}
                              allowNegative={false}
                              placeholder="21,00"
                              className="amount-input"
                            />
                            <ErrorMessage error={fieldErrors[`line_${idx}_vat`]} />
                          </div>

                          <div className="pricing-field">
                            <label className="form-label">Total Invoice with VAT</label>
                            <NumericFormat
                              value={line.sum_with_vat}
                              thousandSeparator="."
                              decimalSeparator=","
                              decimalScale={2}
                              placeholder="0,00"
                              className="amount-input"
                              readOnly
                            />
                          </div>

                          <div className="pricing-field">
                            <label className="form-label">
                              Total System without VAT
                              {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
                            </label>
                            <NumericFormat
                              value={line.sum_system_without_vat}
                              thousandSeparator="."
                              decimalSeparator=","
                              decimalScale={2}
                              placeholder="0,00"
                              className="amount-input"
                              readOnly
                            />
                          </div>

                          <div className="pricing-field">
                            <label className="form-label">
                              Total System with VAT
                              {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
                            </label>
                            <NumericFormat
                              value={line.sum_with_vat_system}
                              thousandSeparator="."
                              decimalSeparator=","
                              decimalScale={2}
                              placeholder="0,00"
                              className="amount-input"
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="invoice-total-preview">
                <div className="preview-line">
                  <span className="invoice-total-label">Total Invoice with VAT:</span>
                  <span className="invoice-total-value">{totalInvoiceDisplay}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="FormInvoiceRegister-section">
          <SectionHeader title="ADDITIONAL DETAILS" section="additional" />

          {expandedSections.additional && (
            <div className="section-content">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Invoice Payment Date</label>
                  <div className="date-input-wrapper">
                    <DatePicker
                      selected={selectedReceiptDueDate}
                      onChange={(date) => handleDateChange(date, "receipt_due_date")}
                      dateFormat="dd.MM.yyyy"
                      locale="ro"
                      placeholderText="Select date"
                      className="FormInvoiceRegister-date-input"
                      popperContainer={({ children }) => (
                        <div style={{ position: "fixed", zIndex: 9999 }}>{children}</div>
                      )}
                    />
                    <span className="calendar-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                  </div>
                  <ErrorMessage error={fieldErrors.receipt_due_date} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Status <span className="required">*</span>
                  </label>
                  <Select
                    options={statusOptions}
                    classNamePrefix="FormInvoiceRegister-select"
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    placeholder="Select status"
                    components={animatedComponents}
                    isClearable
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.status} />
                </div>
              </div>

              <div className="form-group full-width">
                <label className="form-label">Remarks</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="FormInvoiceRegister-description"
                  rows={4}
                  placeholder="Enter remarks..."
                />
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button type="submit" className="primary-btn" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddInvoice;